/**
 * Link an agent: the OAuth consent screen and the device-code entry form
 * (docs/PLAN-AGENT-LINKING.md §2.2).
 *
 * Three ways in, one screen:
 *   /link                       the customer typed the URL, or a headless agent told them to.
 *                               Show the code entry form.
 *   /link?user_code=ABCD-EFGH   they followed `verification_uri_complete`. Prefilled, verified on
 *                               arrival.
 *   /link?request=<id>          their browser was redirected here by /oauth/authorize. The
 *                               request is already parked server-side; fetch it and ask.
 *
 * Everything the screen offers comes from the server: the client's name, the access levels, and
 * the plain-English description of each. The UI cannot offer a level the API would refuse, and the
 * server's list does not contain full access at all.
 *
 * The code-entry half follows Pair.tsx (entry, countdown, terminal state) on purpose: a customer
 * typing a short code into the dashboard should not be able to tell the two flows apart.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { ApiError, api } from "../api/client.js";
import { Banner, ErrorNote, Loading, Spinner } from "../components/ui.js";
import type { CapabilityProfile, ConsentRequest } from "../api/types.js";

/** What happened after the customer answered, when there is nowhere to redirect them to. */
type Outcome = "approved" | "denied" | null;

function secondsUntil(iso: string): number {
  return Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000));
}

function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export default function LinkAgent() {
  // No `me` guard here: this route only renders inside <Protected>, which does not mount its
  // children until a session is resolved, so the old `if (!me) return null` was unreachable.
  const [params] = useSearchParams();
  const requestId = params.get("request");
  const prefilledCode = params.get("user_code");

  const [consent, setConsent] = useState<ConsentRequest | null>(null);
  const [profile, setProfile] = useState<CapabilityProfile | null>(null);
  const [userCode, setUserCode] = useState(prefilledCode ?? "");
  const [loading, setLoading] = useState(Boolean(requestId || prefilledCode));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [outcome, setOutcome] = useState<Outcome>(null);
  /** Set when the API said the request was already answered or expired while this screen was open. */
  const [answered, setAnswered] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  /** A 401 carrying this reason means "prove it is still you", not "you are signed out". */
  const reauthRequired =
    error instanceof ApiError &&
    error.code === "unauthorized" &&
    error.details?.reason === "reauthentication_required";

  const adopt = useCallback((next: ConsentRequest) => {
    setConsent(next);
    setProfile(next.suggestedProfile);
    setSecondsLeft(secondsUntil(next.expiresAt));
    setError(null);
  }, []);

  // Browser leg: the request is already parked, so fetch it and ask.
  useEffect(() => {
    if (!requestId) return;
    let live = true;
    setLoading(true);
    api
      .consentRequest(requestId)
      .then((next) => {
        if (live) adopt(next);
      })
      .catch((e: unknown) => {
        if (live) setError(e);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [requestId, adopt]);

  // Device leg with the code already in the URL: verify it without making them press a button.
  useEffect(() => {
    if (requestId || !prefilledCode) return;
    let live = true;
    setLoading(true);
    api
      .verifyDeviceCode(prefilledCode)
      .then((next) => {
        if (live) adopt(next);
      })
      .catch((e: unknown) => {
        if (live) setError(e);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [requestId, prefilledCode, adopt]);

  // Countdown, so an expired request reads as short-lived rather than broken.
  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const verify = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setBusy(true);
      setError(null);
      try {
        adopt(await api.verifyDeviceCode(userCode.trim()));
      } catch (err) {
        setError(err);
      } finally {
        setBusy(false);
      }
    },
    [userCode, adopt],
  );

  const decide = useCallback(
    async (approve: boolean) => {
      if (!consent) return;
      setBusy(true);
      setError(null);
      try {
        const result = approve
          ? await api.approveConsent(consent.requestId, profile ?? consent.suggestedProfile)
          : await api.denyConsent(consent.requestId);
        // Browser leg: hand control straight back to the agent's redirect URI, carrying the code
        // it is waiting for. Device leg: there is nowhere to go, so say what happened.
        if (result.redirectTo) {
          window.location.assign(result.redirectTo);
          return;
        }
        setOutcome(approve ? "approved" : "denied");
      } catch (err) {
        // 409: somebody answered it, or it expired while the screen was open. There is nothing
        // left to press, so drop the card rather than leave Approve clickable over a dead request.
        if (err instanceof ApiError && (err.code === "conflict" || err.code === "not_found")) {
          setConsent(null);
          setAnswered(true);
        }
        setError(err);
      } finally {
        setBusy(false);
      }
    },
    [consent, profile],
  );

  if (loading) return <Loading label="Checking the request" />;

  const expired = secondsLeft !== null && secondsLeft <= 0;

  /**
   * The request is gone: unknown, already answered, or past its ten minutes. The API answers 404
   * to all three on purpose, so this screen cannot say which, and it must not fall through to the
   * code-entry form either. A customer who arrives on a spent link has no code to type; asking for
   * one reads as a bug on top of a dead end. Same shape as Pair.tsx's not-ready banner.
   */
  const requestGone =
    !consent &&
    error instanceof ApiError &&
    (error.code === "not_found" || error.code === "conflict") &&
    (answered || Boolean(requestId));

  if (requestGone) {
    return (
      <div className="wrap--narrow">
        <div className="page-head">
          <h1>This link request is no longer valid</h1>
          <p className="lede">
            Link requests last ten minutes and can be answered once. This one has expired, or it
            has already been answered. Nothing was granted.
          </p>
        </div>
        <Banner kind="info" title="Start it again from your agent">
          Run the sign-in or link command in your agent once more. It will send you back here with
          a fresh request.
        </Banner>
        <div className="btn-row">
          <RouterLink className="btn btn--ghost" to="/dashboard">
            Back to your Construct
          </RouterLink>
        </div>
      </div>
    );
  }

  if (outcome) {
    // Approving on the device leg does NOT create the connection: the agent is still polling, and
    // the principal is minted when it collects its token. Saying "your agent is linked" here and
    // pointing at a Connections screen with nothing new on it was a promise made a few seconds too
    // early, so the heading says what is true at this instant and the connection link waits.
    return (
      <div className="wrap--narrow">
        <div className="page-head">
          <h1>{outcome === "approved" ? "Approved. Go back to your terminal." : "Request declined"}</h1>
          <p className="lede">
            {outcome === "approved"
              ? "Your agent is waiting for this answer and will pick up its access on its next check, usually within a few seconds. It appears in your connections once it does."
              : "Nothing was granted, and nothing about your Construct changed."}
          </p>
        </div>
        <div className="btn-row">
          <RouterLink className="btn btn--primary" to="/dashboard">
            Back to your Construct
          </RouterLink>
          <RouterLink className="btn btn--ghost" to="/connections">
            See your connections
          </RouterLink>
        </div>
      </div>
    );
  }

  return (
    <div className="wrap--narrow">
      <div className="page-head">
        <h1>Link an agent</h1>
        <p className="lede">
          An agent is asking to reach your Construct. You decide whether it may, and how much it
          gets. You can take it back at any time from your connections.
        </p>
      </div>

      {reauthRequired ? (
        <Banner kind="warn" title="Sign in again to approve this">
          Granting an agent standing access needs a recent sign-in. Sign in once more, then come
          back to this page. <RouterLink to="/sign-in">Sign in</RouterLink>
        </Banner>
      ) : error ? (
        <ErrorNote error={error} />
      ) : null}

      {!consent ? (
        /* ---- device-code entry ------------------------------------------- */
        <div className="card">
          <h2>Enter the code</h2>
          <p className="muted">
            Your agent showed you a short code. Type it here and we will show you what it is asking
            for.
          </p>
          <form onSubmit={verify}>
            <label className="field">
              <span className="field__label">Code</span>
              <input
                className="input mono"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={32}
                placeholder="ABCD-EFGH"
                value={userCode}
                onChange={(e) => setUserCode(e.target.value)}
                style={{ letterSpacing: ".18em", textTransform: "uppercase" }}
              />
              <span className="field__hint">Codes last ten minutes. Case does not matter.</span>
            </label>
            <div className="btn-row">
              <button type="submit" className="btn btn--primary" disabled={busy || userCode.trim().length < 4}>
                {busy ? <Spinner label="Checking" /> : null}
                {busy ? "Checking" : "Continue"}
              </button>
              <RouterLink className="btn btn--ghost" to="/dashboard">
                Not now
              </RouterLink>
            </div>
          </form>
        </div>
      ) : (
        /* ---- consent ------------------------------------------------------ */
        <>
          <div className="card card--accent">
            {/*
              The name is whatever the agent typed when it registered itself, and anyone can
              register. So it is labelled as a claim, not printed as a fact. The one checked thing
              on this card is where the access will be delivered, which this service validated
              against that client's own registration.
            */}
            <h2>An agent calling itself {consent.client.name}</h2>
            <p className="muted">
              That name is what the agent told us about itself when it registered. Nobody has
              checked it, and we cannot tell you who wrote this software. Approve it only if you
              started this yourself, just now.
            </p>
            <p className="muted">
              {consent.client.origin
                ? `If you approve, access is handed back to ${consent.client.origin}, and nowhere else.`
                : "This agent is waiting on the code above; access is handed back to whatever is holding it."}
            </p>
            {consent.userCode ? (
              <p className="mono small muted" style={{ marginTop: 12 }}>
                Code {consent.userCode}
              </p>
            ) : null}
            <p className="mono small muted" style={{ marginTop: 6 }}>
              {expired ? "This request has expired." : `Expires in ${clock(secondsLeft ?? 0)}`}
            </p>
          </div>

          {!consent.constructReady ? (
            <Banner kind="warn" title="Your Construct is not ready yet">
              Linking needs a running Construct. <RouterLink to="/dashboard">Check its status</RouterLink>
            </Banner>
          ) : null}

          <h2 style={{ marginTop: 36, marginBottom: 16 }}>What it may do</h2>
          <div className="stack">
            {consent.offeredProfiles.map((p) => {
              const copy = consent.profileCopy[p] ?? { label: p, description: "" };
              return (
                <label key={p} className="choice" data-selected={profile === p}>
                  <input
                    type="radio"
                    name="capability"
                    value={p}
                    checked={profile === p}
                    onChange={() => setProfile(p)}
                  />
                  <span className="choice__title">{copy.label}</span>
                  <span className="choice__note">{copy.description}</span>
                </label>
              );
            })}
          </div>

          <Banner kind="info" title="What it never gets">
            No agent linked this way can change your billing, read your other connections, or
            delete anything. Full access to your Construct is not on offer here at all.
          </Banner>

          <div className="btn-row">
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || expired || !consent.constructReady}
              onClick={() => void decide(true)}
            >
              {busy ? "Working" : "Approve"}
            </button>
            {/* Nothing to decline on an expired request: the API would 404 and the request is
                already dead. Approve is disabled for the same reason plus the Construct check. */}
            <button
              type="button"
              className="btn btn--danger"
              disabled={busy || expired}
              onClick={() => void decide(false)}
            >
              Decline
            </button>
          </div>
        </>
      )}
    </div>
  );
}
